// =================================================================================
// File: src/layouts/StudioShell.jsx
// Description: Studio owner/manager shell with full provider stack and routes
// This is the complete studio app experience with Vault, Storage, and workspace logic
// =================================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";

// Providers
import { FeedbackProvider } from "../contexts/FeedbackContext";
import { SpeechProvider } from "../contexts/SpeechContext";
import { VoiceTimerProvider } from "../contexts/VoiceTimerContext";
import { StaffProvider } from "../contexts/StaffContext";
import { UtilitiesProvider } from "../contexts/UtilitiesContext";
import { POProvider } from "../contexts/POContext";
import { MaterialProvider } from "../contexts/MaterialContext";
import { KilnProvider } from "../contexts/KilnContext";
import { WorkshopProvider } from "../contexts/WorkshopContext";
import { ParticipantProvider } from "../contexts/ParticipantContext";
import { PackagingProvider } from "../contexts/PackagingContext";
import { MarketingProvider } from "../contexts/MarketingContext";
import { useVault } from "../contexts/VaultContext";
import { NotificationsProvider } from "../contexts/NotificationsContext";
import { ForumSubscriptionsProvider } from "../contexts/ForumSubscriptionsContext";
import { PlannerProvider } from "../contexts/PlannerContext";
import { TasksProvider } from "../contexts/TasksContext";
import { StreaksProvider, useStreaks } from "../contexts/StreaksContext";
import { useAuth } from "../contexts/AuthContext";
import { StudioSpaceProvider } from "@/contexts/StudioSpaceContext";
import { StudioProvider, useStudio } from "../contexts/StudioContext";
import { PublicStudioProvider } from "../contexts/PublicStudioContext";
import { InspirationProvider } from "../contexts/InspirationContext";
import { AssistantProvider } from "../contexts/AssistantContext";
import { PricingSettingsProvider, defaultPricingSettings } from "../contexts/PricingSettingsContext";
import { useAccess } from "../contexts/AccessContext";
import { useEntitlements } from "../contexts/EntitlementsContext";
import TermsModal from "../components/Modals/TermsModal";
import { useTermsAcceptance } from "../hooks/useTermsAcceptance";
import { useNotifications } from "../contexts/NotificationsContext";
import { invalidateEntitlements } from "../lib/entitlementsCache";

// Storage
import { useStorage } from "../contexts/StorageContext";

// Common Components
import ToastHub from "../components/Common/ToastHub";
import TimerTray from "../components/Timers/TimerTray";
import Footer from "../components/Common/Footer";
import TopBar from "../components/TopBar/TopBar";
import ErrorBoundary from "../components/Common/ErrorBoundary";
import KeyShield from "../components/Common/KeyShield";
import HelpDrawer from "../components/Help/HelpDrawer";

// AI Components
import LowStockSuggester from "../components/AI/LowStockSuggester";
import MudoAssistant from "../components/AI/MudoAssistant";

// Modals
import CustomizeModal from "../components/Modals/CustomizeModal";
import ManageWidgetsModal from "../components/Modals/ManageWidgetsModal";
import SubscriptionModal from "../components/Modals/SubscriptionModal";
import CreateBatchModal from "../components/Modals/CreateBatchModal";
import FolderConnectModal from "../components/Modals/FolderConnectModal";
import MudoCanvaModal from "../components/Modals/MudoCanvaModal";

// Screens & Pages
import Home from "../components/Screens/Home";
import Dashboard from "../components/Screens/Dashboard";
import ProductGallery from "../components/Costplanner/ProductGallery";
import AdvancedCalculator from "../components/Costplanner/AdvancedCalculator";
import Planner from "../components/Planner/Planner";
import GanttView from "../components/Planner/PlannerViews/GanttView";
import MaterialFinishScreen from "../components/Costplanner/MaterialFinishScreen";
import POList from "../components/Purchase-orders/POList";
import StaffPage from "../components/Staff/StaffPage";
import ProductionSettings from "../components/Production/ProductionSettings";
import BatchManager from "../components/Batch/BatchManager";
import BatchView from "../components/Batch/BatchView";
import KilnLogsPage from "../components/Batch/KilnLogsPage";
import KilnSettingsPanel from "../components/Kiln/KilnSettingsPanel";
import KilnPlanner from "../components/Kiln/KilnPlanner";
import Receipts from "../components/Receipts/Receipts";
import WorkshopPage from "../components/Screens/WorkshopPage";
import MyPieces from "../components/Screens/MyPieces";
import ParticipantsPage from "../components/Screens/ParticipantsPage";
import Journal from "../components/Screens/Journal";
import MarketsChecklistPage from "../components/Screens/MarketsChecklistPage";
import TimeLogs from "../components/Timers/TimeLogs";
import StudioAnalytics from "../pages/StudioAnalytics";
import SettingsSoundHaptics from "../pages/SettingsSoundHaptics";
import StaffTargets from "../pages/StaffTargets";
import Shop from "../pages/Shop";
import useStripePayoutsGuard from "../hooks/useStripePayoutsGuard";
import { useActiveStudioId } from "../hooks/useActiveStudioId";
import Forum from "../pages/Forum";
import Help from "../pages/Help";
import BillingSuccess from "../pages/BillingSuccess";
import PackagingLibrary from "../components/Packaging/PackagingLibrary";
import PackagingRules from "../components/Packaging/PackagingRules";
import DataAndBackups from "../components/Settings/DataAndBackups";
import InspirationPage from "../components/Screens/InspirationPage";
import ExplorePage from "../pages/ExplorePage";
import NotificationsInbox from "../pages/NotificationsInbox";
import TemplatesPage from "../pages/TemplatesPage";
import StorageDebug from "../pages/StorageDebug";
import StoragePanel from "../components/Settings/StoragePanel";
import NotFound from "../pages/NotFound";

// Guards
import { RequireSelectedProduct } from "../routes/guards";

// Utils
import { resolvePlanDefaults } from "../config/plans";
import { supabase } from "../supabaseClient";

// Offline bypass helper: detect cached Supabase auth token in localStorage
function hasCachedSupabaseAuthToken() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) {
        const v = localStorage.getItem(k);
        if (v && v.length > 10) return true;
      }
    }
  } catch {}
  return false;
}

// Constants
const LAST_SYNC_KEY = "mudoLastFolderSync";

// Default Settings
const defaultSettings = {
  businessName: "Bark Line Studio",
  logoUrl: null,
  website: "",
  phone: "",
  isDarkMode: true,
  country: "Australia",
  region: "",
  location: "",
  bio: "",
  practiceTypes: [],
  theme: "Burnt Banksia",
  currency: "AUD",
  weight: "g",
  volume: "ml",
  size: "mm",
  favorites: [],
  widgets: ["CostPlanner", "MaterialDetails", "ReorderPO"],
  widgetsLayout: null,
  productionOutput: 100,
  studioComplexityFactor: 1.0,
  kiln: { maxConcurrent: 1 },
  pricingSettings: { ...defaultPricingSettings },
};

function normalizeSettings(stored) {
  if (!stored) return { ...defaultSettings };
  const s = { ...stored };
  if (!s.logoUrl && s.studioLogo) s.logoUrl = s.studioLogo;
  if (!s.website && s.site) s.website = s.site;
  if (!s.website && s.websiteUrl) s.website = s.websiteUrl;
  if (!s.phone && s.contactNumber) s.phone = s.contactNumber;
  if (!s.productionOutput && s.units) s.productionOutput = Number(s.units) || 0;
  if (!s.productionOutput && s.defaultUnits) s.productionOutput = Number(s.defaultUnits) || 0;
  return {
    ...defaultSettings,
    ...s,
    pricingSettings: { ...defaultPricingSettings, ...(s.pricingSettings || {}) },
  };
}

/* =============================
   Vault-backed state helper (debounced, hydrate-safe)
   ============================= */
function useVaultedState(key, initialValue) {
  const { ready, getJSON, setJSON } = useVault();
  const [value, setValue] = useState(initialValue);

  const saveTimerRef = useRef(null);
  const hydratingRef = useRef(false);
  const lastSavedRef = useRef(undefined);

  // initial / on-ready load (hydrate once)
  useEffect(() => {
    let active = true;
    if (!ready) return;
    (async () => {
      hydratingRef.current = true;
      try {
        const stored = await getJSON(key, initialValue);
        const normalized =
          key === "settings" ? normalizeSettings(stored ?? initialValue) : stored ?? initialValue;
        if (active) {
          setValue(normalized);
          try {
            lastSavedRef.current = JSON.stringify(normalized);
          } catch {
            lastSavedRef.current = undefined;
          }
        }
      } finally {
        hydratingRef.current = false;
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, key]);

  // debounced persist on change (skip while hydrating; skip if unchanged)
  useEffect(() => {
    if (!ready) return;
    if (hydratingRef.current) return;

    let nextStr = null;
    try {
      nextStr = JSON.stringify(value);
      if (nextStr === lastSavedRef.current) return;
    } catch {
      // if stringify fails we still save (debounced)
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await setJSON(key, value);
        if (nextStr) lastSavedRef.current = nextStr;
      } catch {
        // non-fatal; don't block UI
      }
    }, key === "settings" ? 400 : 250);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [ready, key, value, setJSON]);

  // refresh after Receive
  useEffect(() => {
    if (!ready) return;
    const onRestored = async () => {
      hydratingRef.current = true;
      try {
        const stored = await getJSON(key, initialValue);
        const normalized =
          key === "settings" ? normalizeSettings(stored ?? initialValue) : stored ?? initialValue;
        setValue(normalized);
        try {
          lastSavedRef.current = JSON.stringify(normalized);
        } catch {
          lastSavedRef.current = undefined;
        }
      } finally {
        hydratingRef.current = false;
      }
    };
    window.addEventListener("mudo:vault-restored", onRestored);
    return () => window.removeEventListener("mudo:vault-restored", onRestored);
  }, [ready, key, getJSON]);

  return [value, setValue];
}

/**
 * StudioSettingsSync - Syncs StudioContext (Supabase data) to settings state
 * This ensures components reading from settings get the latest Supabase data
 */
function StudioSettingsSync({ setSettings }) {
  const { studio } = useStudio() || {};
  
  useEffect(() => {
    if (!studio) return;
    
    // Merge studio data from Supabase into settings
    setSettings(prev => ({
      ...prev,
      // Customize Studio fields from Supabase
      businessName: studio.businessName || prev.businessName,
      logoUrl: studio.logoUrl || prev.logoUrl,
      website: studio.website || prev.website,
      country: studio.country || prev.country,
      region: studio.region || prev.region,
      location: studio.location || prev.location,
      bio: studio.bio || prev.bio,
      practiceTypes: studio.practiceTypes || prev.practiceTypes,
      currency: studio.currency || prev.currency,
      weight: studio.weight || prev.weight,
      volume: studio.volume || prev.volume,
      size: studio.size || prev.size,
      productionOutput: studio.productionOutput ?? prev.productionOutput,
      theme: studio.theme || prev.theme,
      isDarkMode: studio.isDarkMode ?? prev.isDarkMode,
      email: studio.email || prev.email,
      taxName: studio.taxName ?? prev.taxName,
      taxRatePct: studio.taxRatePct ?? prev.taxRatePct,
      taxModeDefault: studio.taxModeDefault || prev.taxModeDefault,
      pricingSettings: studio.pricingSettings || prev.pricingSettings,
    }));
  }, [studio, setSettings]);
  
  return null;
}

/**
 * StudioShell - Complete studio management application
 * 
 * Features:
 * - Full provider stack including Vault, Storage, Staff, Workshops, etc.
 * - Workspace/Drive connection logic (FolderConnectModal)
 * - All studio routes (dashboard, planner, batches, etc.)
 * - AI assistant, timers, analytics, and more
 */
export default function StudioShell() {
    const { isStripeConnected, ensureStripeConnected } = useStripePayoutsGuard();
  const { studioId } = useActiveStudioId();
    const [hideStripeBanner, setHideStripeBanner] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logVisit } = useStreaks() || {};
  const {
    signOut,
    user,
    userProfile,
    onboardingStatus,
    upsertUserProfile,
    isAuthReady,
  } = useAuth() || {};
  const { mode, hasFolder, baseDirName, exportAll, importBackup } = useStorage();
  const notifCtx = useNotifications();
  const refreshSmartNotifications = notifCtx?.refreshSmartNotifications;

  const hydratingRef = useRef(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);

  const [settings, setSettings] = useVaultedState("settings", defaultSettings);
  const [products, setProducts] = useVaultedState("products", []);
  const [tasks, setTasks] = useVaultedState("tasks", []);
  const [currentPlan, setCurrentPlan] = useVaultedState("subscriptionPlan", "free");

  // DEV override: allow forcing a plan in development via Vite env var
  useEffect(() => {
    try {
      if (import.meta.env.DEV) {
        const forced = import.meta.env.VITE_FORCE_PLAN;
        if (forced && String(forced).trim()) {
          setCurrentPlan(String(forced).trim());
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!refreshSmartNotifications) return;
    refreshSmartNotifications();
  }, [refreshSmartNotifications]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);
  const [customizeIntent, setCustomizeIntent] = useState(null);
  const [showManage, setShowManage] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [batchVersion, setBatchVersion] = useState(0);
  const [batchToEdit, setBatchToEdit] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [isCanvaOpen, setIsCanvaOpen] = useState(false);
  const { loading: termsLoading, needsAcceptance, acceptTerms } = useTermsAcceptance();
  const [studioSetupLoading, setStudioSetupLoading] = useState(false);
  const [studioSetupError, setStudioSetupError] = useState("");
  const [studioRecord, setStudioRecord] = useState(null);
  const [forceCustomize, setForceCustomize] = useState(false);
  const [seededDefaults, setSeededDefaults] = useState(false);

  // Get access state early for offline bypass calculation
  const { isUnlocked, refreshAccess } = useAccess() || {};
  const { accessSource, isLocked } = useEntitlements() || {};
  const wasUnlockedRef = useRef(isUnlocked);

  // Track online/offline state for modal gating
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Compute offline bypass condition (allow if user was previously unlocked OR is currently cached as unlocked)
  const hasLastUnlocked = localStorage.getItem("mudo:lastUnlocked") === "1";
  const hasCachedAuth = hasCachedSupabaseAuthToken();
  const offlineBypass = !isOnline && (hasLastUnlocked || isUnlocked) && hasCachedAuth;

  // Debug logging for offline bypass in StudioShell
  useEffect(() => {
    if (!isOnline) {
      console.log('[StudioShell] Offline mode detected:', {
        isOnline,
        hasLastUnlocked,
        hasCachedAuth,
        isUnlocked,
        offlineBypass
      });
    }
  }, [isOnline, hasLastUnlocked, hasCachedAuth, isUnlocked, offlineBypass]);

  // Force-close subscription/manage modals when offline bypass is active
  useEffect(() => {
    if (offlineBypass) {
      if (showSubscription) {
        console.log('[StudioShell] Closing subscription modal (offline bypass active)');
        setShowSubscription(false);
      }
      if (showManage) {
        console.log('[StudioShell] Closing manage modal (offline bypass active)');
        setShowManage(false);
      }
    }
  }, [offlineBypass, showSubscription, showManage]);

  // Auto-close soft modal if access becomes valid (user redeemed promo or completed checkout)
  // Only close if modal was already open and user just became unlocked
  useEffect(() => {
    if (!wasUnlockedRef.current && isUnlocked && showSubscription) {
      // User just became unlocked while modal was open -> close it
      console.log('[StudioShell] Auto-closing subscription modal (user just unlocked)');
      setShowSubscription(false);
    }
    wasUnlockedRef.current = isUnlocked;
  }, [isUnlocked, showSubscription]);

  // Debug: log when showSubscription changes
  useEffect(() => {
    console.log('[StudioShell] showSubscription changed to:', showSubscription);
  }, [showSubscription]);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", settings.theme);
    if (settings.isDarkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [settings.theme, settings.isDarkMode]);

  // Show folder modal if no folder connected
  useEffect(() => {
    if (!hasFolder) setShowFolderModal(true);
    else setShowFolderModal(false);
  }, [hasFolder]);

  // Count visit for streaks
  useEffect(() => {
    try {
      if (logVisit) logVisit();
    } catch {}
  }, [logVisit]);

  // Stripe return: open Customize modal once so UI refreshes and user sees updated badge.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      if (params.get("stripe") !== "done") return;

      setShowCustomize(true);

      params.delete("stripe");
      const path = location.pathname + (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", path);
    } catch {}
  }, [location.pathname, location.search]);

  // Allow other components to request opening Customize (e.g., Mudo Map widget)
  useEffect(() => {
    const handler = (e) => {
      setCustomizeIntent(e?.detail || null);
      setShowCustomize(true);
    };
    window.addEventListener("mudo:open-customize-modal", handler);
    return () => window.removeEventListener("mudo:open-customize-modal", handler);
  }, []);

  const needsStudioSetup = useMemo(() => {
    if (!user?.id) return false;
    if (!supabase) return false;
    if (studioSetupLoading) return false;
    if (!studioRecord) return true;
    if (
      typeof studioRecord.setup_status === "string" &&
      studioRecord.setup_status.toLowerCase() === "complete"
    ) {
      return false;
    }
    return true;
  }, [user?.id, studioSetupLoading, studioRecord]);

  // Fetch studio setup state from Supabase (first-time Studio World guard)
  useEffect(() => {
    let active = true;
    if (!user?.id || !isAuthReady) return;
    if (!supabase) return;

    setStudioSetupLoading(true);
    setStudioSetupError("");

    (async () => {
      try {
        const { data, error, status } = await supabase
          .from("studios")
          .select("*")
          .eq("owner_user_id", user.id)
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle();

        if (!active) return;
        if (error && status !== 406) throw error;
        const record = status === 406 ? null : data || null;
        setStudioRecord(record);

        const incomplete =
          !record ||
          (typeof record.setup_status === "string"
            ? record.setup_status.toLowerCase() !== "complete"
            : true);
        if (incomplete) {
          setForceCustomize(true);
          setShowCustomize(true);
        }
      } catch (err) {
        if (!active) return;
        console.warn("[StudioShell] Failed to load studio setup state", err);
        setStudioSetupError(err?.message || "Could not load studio setup.");
      } finally {
        if (active) setStudioSetupLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [user?.id, isAuthReady]);

  // Seed defaults for first-time setup from user profile
  useEffect(() => {
    if (!needsStudioSetup || seededDefaults) return;
    setSeededDefaults(true);
    setSettings((prev) => {
      const next = { ...prev };
      if (
        (!prev.businessName || prev.businessName === defaultSettings.businessName) &&
        (userProfile?.last_name || userProfile?.first_name)
      ) {
        const base =
          userProfile?.last_name ||
          userProfile?.first_name ||
          prev.businessName ||
          "Studio";
        next.businessName = `${base} Studio`;
      }
      if (!prev.country && userProfile?.location_country) {
        next.country = userProfile.location_country;
      }
      if (!prev.region && userProfile?.location_city) {
        next.region = userProfile.location_city;
      }
      return next;
    });
  }, [needsStudioSetup, seededDefaults, setSettings, userProfile]);

  const handleStudioSetupComplete = async (payload) => {
    if (!supabase) throw new Error("Supabase not configured");
    if (!user?.id) throw new Error("No user available for studio setup");

    const studioPayload = {
      owner_user_id: user.id,
      name: payload.businessName || "My Studio",
      location: payload.region || payload.country || "",
      time_zone: payload.timeZone || payload.timezone || null,
      default_currency: payload.currency || null,
      theme: payload.theme || null,
      setup_status: "complete", // ensure column exists in studios table
    };

    const { data, error } = await supabase
      .from("studios")
      .upsert(studioPayload, { onConflict: "owner_user_id" })
      .select()
      .maybeSingle();

    if (error) {
      console.warn("[StudioShell] studio upsert failed", error);
      throw error;
    }

    setStudioRecord(data);
    setForceCustomize(false);
    setShowCustomize(false);
    setStudioSetupError("");

    try {
      await upsertUserProfile?.({ onboarding_status: "complete" });
    } catch (err) {
      console.warn("[StudioShell] Failed to update onboarding_status", err);
    }

    return data;
  };

  // Handle Stripe onboarding completion - refresh studio data
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || window.location.search);
      if (params.get("stripe") === "done") {
        console.log("[StudioShell] Stripe onboarding completed, refreshing studio data");
        
        // Force refresh of studio context by triggering a re-query
        // This is handled by StudioContext itself, just clear the flag
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("stripe");
          window.history.replaceState({}, "", url.toString());
        } catch {}
      }
    } catch (e) {
      console.warn("[StudioShell] Error handling stripe=done", e);
    }
  }, [location.search]);

  // Handle Stripe Checkout success redirect
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || window.location.search);
      if (params.get("session_id")) {
        const sessionId = params.get("session_id");

        try {
          const raw = sessionStorage.getItem("mudo:confirmedSessions");
          const processed = raw ? JSON.parse(raw) : [];
          if (Array.isArray(processed) && processed.includes(sessionId)) {
            try {
              const url = new URL(window.location.href);
              url.searchParams.delete("checkout");
              url.searchParams.delete("session_id");
              window.history.replaceState({}, "", url.toString());
            } catch {}
            return;
          }
        } catch (e) {}

        (async () => {
          try {
            const res = await fetch(
              `/api/checkout/confirm?session_id=${encodeURIComponent(sessionId)}`
            );
            const json = await res.json();
            if (res.ok && json?.ok && json.plan) {
              setCurrentPlan(json.plan);

              // Invalidate entitlements cache to force fresh fetch
              try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user?.id) {
                  invalidateEntitlements(user.id);
                }
              } catch (err) {
                console.error("[StudioShell] failed to invalidate cache:", err);
              }

              // Refresh access state so AccessContext updates immediately
              try {
                await refreshAccess?.();
              } catch (err) {
                console.error("[StudioShell] refreshAccess failed:", err);
              }

              try {
                const autoApply = !!(import.meta.env.VITE_AUTO_APPLY_PLAN_DEFAULTS);
                if (autoApply) {
                  const cfg = resolvePlanDefaults(json.plan, defaultSettings.productionOutput);
                  setSettings((s) => ({ ...s, productionOutput: cfg.productionOutput, studioComplexityFactor: cfg.studioComplexityFactor }));
                }
              } catch (e) {}

              try {
                const raw = sessionStorage.getItem("mudo:confirmedSessions");
                const processed = raw ? JSON.parse(raw) : [];
                const next = Array.isArray(processed) ? processed.concat(sessionId) : [sessionId];
                sessionStorage.setItem("mudo:confirmedSessions", JSON.stringify(next));
              } catch {}
              window.dispatchEvent?.(
                new CustomEvent("mudo:toast", {
                  detail: { type: "success", message: "Subscription updated" },
                })
              );
            } else {
              window.dispatchEvent?.(
                new CustomEvent("mudo:toast", {
                  detail: { type: "error", message: json?.error || "Could not confirm subscription" },
                })
              );
            }
          } catch (err) {
            console.error("[Mudo] checkout confirm error", err);
          } finally {
            try {
              const url = new URL(window.location.href);
              url.searchParams.delete("checkout");
              url.searchParams.delete("session_id");
              window.history.replaceState({}, "", url.toString());
            } catch {}
          }
        })();
      }
    } catch (e) {}
  }, [location.search, setCurrentPlan, setSettings]);

  // Apply plan defaults event listener
  useEffect(() => {
    function onApply(e) {
      try {
        const plan = e && e.detail && e.detail.plan ? String(e.detail.plan).toLowerCase() : null;
        const cfg = resolvePlanDefaults(plan, defaultSettings.productionOutput);
        setSettings((s) => ({ ...s, productionOutput: cfg.productionOutput, studioComplexityFactor: cfg.studioComplexityFactor }));
        window.dispatchEvent(new CustomEvent("mudo:toast", { detail: { type: "success", message: "Plan defaults applied" } }));
      } catch (err) {
        window.dispatchEvent(new CustomEvent("mudo:toast", { detail: { type: "error", message: "Could not apply plan defaults" } }));
      }
    }
    window.addEventListener("mudo:apply-plan-defaults", onApply);
    return () => window.removeEventListener("mudo:apply-plan-defaults", onApply);
  }, [setSettings]);

  // Unsaved indicator
  useEffect(() => {
    if (hydratingRef.current) return;
    const t = setTimeout(() => setHasUnsaved(true), 500);
    return () => clearTimeout(t);
  }, [settings, products, tasks]);

  async function handleBackupNow() {
    try {
      hydratingRef.current = true;
      const res = await exportAll();
      const nowIso = new Date().toISOString();
      try {
        localStorage.setItem(LAST_SYNC_KEY, nowIso);
      } catch {}
      setHasUnsaved(false);
      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", {
          detail: { type: "success", message: `Backup saved via ${res.where} (${res.name})` },
        })
      );
    } catch (err) {
      console.error("[Mudo] Backup error:", err);
      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", { detail: { type: "error", message: "Backup failed" } })
      );
    } finally {
      hydratingRef.current = false;
    }
  }

  async function handleRestoreSelected(ev) {
    const f = ev?.target?.files?.[0];
    if (!f) return;
    try {
      hydratingRef.current = true;
      const res = await importBackup(f);
      window.dispatchEvent(new Event("mudo:restore-complete"));
      setHasUnsaved(false);
      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", {
          detail: { type: "success", message: `Restored ${res.keys} items from backup` },
        })
      );
    } catch (err) {
      console.error("[Mudo] Restore error:", err);
      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", { detail: { type: "error", message: "Restore failed" } })
      );
    } finally {
      hydratingRef.current = false;
      if (ev?.target) ev.target.value = "";
    }
  }

  async function handleLogout() {
    try {
      await signOut?.();
    } catch (err) {
      console.error("[Mudo] sign out error", err);
    }
    localStorage.removeItem("mudo:userRole");
    navigate("/auth", { replace: true });
  }

  const handleSelectPlan = (plan) => {
    setCurrentPlan(plan);
    setShowSubscription(false);
  };

  const onAddTasksToPlanner = (newTasks) => {
    const all = [...tasks, ...(Array.isArray(newTasks) ? newTasks : [])];
    setTasks(all);
  };
  const onBatchCreated = () => setBatchVersion((v) => v + 1);
  const handleViewBatch = (batchId) => navigate(`/batches/${batchId}`);

  useEffect(() => {
    try {
      localStorage.setItem("mudoTasks", JSON.stringify(tasks || []));
      window.dispatchEvent(new Event("mudo:tasks-updated"));
    } catch {}
  }, [tasks]);


  const workspaceTag = (
    <div style={{ position: "fixed", bottom: 10, right: 12, fontSize: 11, opacity: 0.7 }}>
      Storage: {mode || "none"}
      {hasFolder ? "" : " • not linked"}
      {hydratingRef.current ? " • working…" : ""}
      {hasUnsaved ? " • unsaved changes" : ""}
      {baseDirName ? ` • ${baseDirName}` : ""}
    </div>
  );

  // Free plan limits
  // During active trial (unlocked), user has FULL ACCESS - no restrictions
  // Only apply 3-product limit AFTER trial expires (locked and no paid subscription)
  const isFree = isLocked && String(currentPlan || "free").toLowerCase() === "free";
  const FREE_LIMIT = 3;
  function nudgeUpgrade(message = `Free plan limit reached (${FREE_LIMIT} products).`) {
    try {
      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", { detail: { type: "info", message } })
      );
    } catch {}
    setShowSubscription(true);
  }

  return (
    <StudioProvider>
      <PublicStudioProvider>
        <StudioSettingsSync setSettings={setSettings} />
        <PricingSettingsProvider settings={settings} setSettings={setSettings}>
          <FeedbackProvider>
          <NotificationsProvider>
            <ForumSubscriptionsProvider>
            <StreaksProvider>
              <PlannerProvider>
                <TasksProvider>
                  <SpeechProvider>
                  <VoiceTimerProvider>
                    <MaterialProvider>
                      <StaffProvider>
                        <KilnProvider>
                          <UtilitiesProvider>
                            <MarketingProvider>
                              <POProvider>
                                <WorkshopProvider>
                                  <ParticipantProvider>
                                    <PackagingProvider>
                                      <StudioSpaceProvider>
                                        <InspirationProvider>
                                          <AssistantProvider>
                                            <FolderConnectModal
                                              open={showFolderModal}
                                              onClose={() => setShowFolderModal(false)}
                                            />
                                            <div className="app-shell" style={{ minHeight: "100dvh", background: "#0f0f10", color: "#fff", display: "flex", flexDirection: "column" }}>
                                              <div className="app-main" style={{ flex: 1 }}>
                                              <TopBar
                                                onCustomizeStudio={() => {
                                                  setForceCustomize(false);
                                                  setShowCustomize(true);
                                                }}
                                                onChoosePlan={() => setShowSubscription(true)}
                                                onLogout={handleLogout}
                                                onConnectFolder={() => setShowFolderModal(true)}
                                                onOpenCanva={() => setIsCanvaOpen(true)}
                                                currentPlan={currentPlan}
                                              />

                                              {/* Global Stripe banner when not connected */}
                                              {!isStripeConnected && !hideStripeBanner && (
                                                <div className="mx-4 mt-4 p-3 rounded-lg bg-amber-600/20 border border-amber-500/40">
                                                  <div className="flex items-center justify-between">
                                                    <div>
                                                      <div className="text-sm font-semibold">Payouts not set up</div>
                                                      <div className="text-xs text-white/80">To receive payments for offers, workshops and studio space bookings, connect your Stripe account.</div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <button
                                                        className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                                        onClick={async () => {
                                                          const connectStudioId = studioId || studioRecord?.id || studioRecord?.studio_id || null;
                                                          const res = await ensureStripeConnected({ studioId: connectStudioId });
                                                          if (!res?.ok && res?.error) {
                                                            window.dispatchEvent?.(
                                                              new CustomEvent("mudo:toast", { detail: { type: "error", message: res.error } })
                                                            );
                                                          }
                                                        }}
                                                      >
                                                        Connect Stripe
                                                      </button>
                                                      <button
                                                        className="px-2 py-1 rounded bg-black/30 border border-white/20 text-white/70 text-xs"
                                                        onClick={() => setHideStripeBanner(true)}
                                                        title="Dismiss for this session"
                                                      >
                                                        Dismiss
                                                      </button>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}

                                              <KeyShield />

                                              {showCustomize && (
                                                <CustomizeModal
                                                  settings={settings}
                                                  updateSettings={setSettings}
                                                  onFinished={handleStudioSetupComplete}
                                                  forceBlocking={forceCustomize}
                                                  initialTab={customizeIntent?.tab}
                                                  initialShowMapPicker={!!customizeIntent?.mapPicker}
                                                  onClose={() => {
                                                    if (forceCustomize) return;
                                                    setShowCustomize(false);
                                                    setCustomizeIntent(null);
                                                  }}
                                                />
                                              )}
                                              {showManage && !offlineBypass && (
                                                <ManageWidgetsModal
                                                  isOpen={showManage}
                                                  widgets={settings.widgets}
                                                  updateWidgets={(w) =>
                                                    setSettings({ ...settings, widgets: w })
                                                  }
                                                  onClose={() => setShowManage(false)}
                                                />
                                              )}
                                              {showCreateBatch && (
                                                <CreateBatchModal
                                                  onClose={(reload) => {
                                                    setShowCreateBatch(false);
                                                    if (reload) onBatchCreated();
                                                    setBatchToEdit(null);
                                                  }}
                                                  products={products}
                                                  batchToEdit={batchToEdit}
                                                />
                                              )}
                                              {!offlineBypass && (
                                                <SubscriptionModal
                                                  isOpen={showSubscription}
                                                  onClose={() => setShowSubscription(false)}
                                                  onSelectPlan={handleSelectPlan}
                                                  currentPlan={currentPlan}
                                                />
                                              )}
                                              <MudoCanvaModal
                                                isOpen={isCanvaOpen}
                                                onClose={() => setIsCanvaOpen(false)}
                                                onSaveToMudo={(exportMeta) => {
                                                  console.log("Canva design saved:", exportMeta);
                                                  // TODO: Integrate with StorageContext to save designs
                                                  alert("Design saved to workspace!\n\nFull integration with storage coming soon.");
                                                }}
                                              />

                                              <main style={{ padding: 16 }}>
                                                <ErrorBoundary>
                                                  <React.Suspense fallback={<div>Loading…</div>}>
                                                    <Routes>
                                                      <Route
                                                        path="home"
                                                        element={
                                                          <Home
                                                            settings={settings}
                                                            updateSettings={setSettings}
                                                            onEnterStudio={(dest) => {
                                                              if (dest === "plan") navigate("/dashboard/planner/tasks");
                                                              else if (dest === "batches") navigate("/dashboard/batches");
                                                              else navigate("/dashboard");
                                                            }}
                                                          />
                                                        }
                                                      />
                                                      <Route
                                                        path="/"
                                                        element={
                                                          <Dashboard
                                                            settings={settings}
                                                            updateSettings={setSettings}
                                                            onHome={() => navigate("/dashboard/home")}
                                                            onOpenGallery={() => navigate("/dashboard/gallery")}
                                                            onManageWidgets={() => setShowManage(true)}
                                                            onOpenMaterials={() => navigate("/dashboard/materials")}
                                                            onProductsChange={setProducts}
                                                            onOpenCreateBatch={() => {
                                                              setBatchToEdit(null);
                                                              setShowCreateBatch(true);
                                                            }}
                                                            hideInternalHeader
                                                            tasks={tasks}
                                                            setTasks={setTasks}
                                                          />
                                                        }
                                                      />
                                                      <Route
                                                        path="gallery"
                                                        element={
                                                          <ProductGallery
                                                            settings={settings}
                                                            products={products}
                                                            currentPlan={currentPlan}
                                                            isFreeOverride={isFree}
                                                            onRequireUpgrade={() => nudgeUpgrade()}
                                                            onBack={() => navigate("/dashboard")}
                                                            onOpenPlanner={(prod) => {
                                                              setSelectedProduct(prod);
                                                              navigate("/dashboard/planner");
                                                            }}
                                                            onCreateNew={() => {
                                                              if (isFree && (products?.length || 0) >= FREE_LIMIT) {
                                                                nudgeUpgrade();
                                                                return;
                                                              }
                                                              const blank = { id: Date.now().toString(), name: "" };
                                                              setSelectedProduct(blank);
                                                              navigate("/dashboard/planner");
                                                            }}
                                                            onAddTasksToPlanner={onAddTasksToPlanner}
                                                            onProductsChange={setProducts}
                                                          />
                                                        }
                                                      />
                                                      <Route
                                                        path="planner"
                                                        element={
                                                          <RequireSelectedProduct selectedProduct={selectedProduct}>
                                                            <AdvancedCalculator
                                                              product={selectedProduct}
                                                              settings={settings}
                                                              updateProduct={setSelectedProduct}
                                                              onSaveProduct={(prod) => {
                                                                const safeProd = {
                                                                  ...prod,
                                                                  id: prod.id || Date.now().toString(),
                                                                  name: (prod.name || "Unnamed Product").trim(),
                                                                };
                                                                const updated = [...products];
                                                                const idx = updated.findIndex(
                                                                  (p) => String(p.id) === String(safeProd.id)
                                                                );

                                                                if (idx < 0 && isFree && (products?.length || 0) >= FREE_LIMIT) {
                                                                  nudgeUpgrade();
                                                                  return;
                                                                }

                                                                if (idx >= 0) updated[idx] = safeProd;
                                                                else updated.push(safeProd);
                                                                setProducts(updated);
                                                                // Don't navigate away - stay on calculator page
                                                              }}
                                                              onBack={() => navigate("/dashboard")}
                                                            />
                                                          </RequireSelectedProduct>
                                                        }
                                                      />
                                                      <Route
                                                        path="planner/tasks"
                                                        element={
                                                          <Planner
                                                            settings={settings}
                                                            tasks={tasks}
                                                            setTasks={setTasks}
                                                            onBack={() => navigate("/dashboard")}
                                                          />
                                                        }
                                                      />
                                                      <Route
                                                        path="planner/gantt"
                                                        element={<GanttView tasks={tasks} setTasks={setTasks} settings={settings} />}
                                                      />
                                                      <Route
                                                        path="batches"
                                                        element={
                                                          <BatchManager
                                                            onBack={() => navigate("/dashboard")}
                                                            onOpenCreateBatch={(batch) => {
                                                              setBatchToEdit(batch || null);
                                                              setShowCreateBatch(true);
                                                            }}
                                                            batchVersion={batchVersion}
                                                            onViewBatch={handleViewBatch}
                                                          />
                                                        }
                                                      />
                                                      <Route path="/batches/:batchId" element={<BatchView />} />
                                                      <Route path="kiln-planner" element={<KilnPlanner />} />
                                                      <Route path="kilns" element={<KilnSettingsPanel />} />
                                                      <Route path="kiln-logs" element={<KilnLogsPage />} />
                                                      <Route path="materials" element={<MaterialFinishScreen />} />
                                                      <Route path="purchase-orders" element={<POList />} />
                                                      <Route path="billing/success" element={<BillingSuccess />} />
                                                      <Route path="staff" element={<StaffPage />} />
                                                      <Route path="settings" element={<ProductionSettings />} />
                                                      <Route path="/settings/storage" element={<StoragePanel />} />
                                                      <Route path="receipts" element={<Receipts />} />
                                                      <Route
                                                        path="workshops"
                                                        element={
                                                          <WorkshopPage
                                                            isDark={settings.isDarkMode}
                                                            onBack={() => navigate("/dashboard")}
                                                          />
                                                        }
                                                      />
                                                      <Route path="participants" element={<ParticipantsPage />} />
                                                      <Route path="my-pieces" element={<MyPieces />} />
                                                      <Route path="time-logs" element={<TimeLogs />} />
                                                      <Route path="analytics" element={<StudioAnalytics />} />
                                                      <Route path="/settings/sound" element={<SettingsSoundHaptics />} />
                                                      <Route path="/settings/staff-targets" element={<StaffTargets />} />
                                                      <Route path="help" element={<Help />} />
                                                      <Route path="notifications" element={<NotificationsInbox />} />
                                                    <Route path="shop" element={<Shop />} />
                                                      <Route path="forum" element={<Forum />} />
                                                      <Route path="packaging/library" element={<PackagingLibrary />} />
                                                      <Route path="packaging/rules" element={<PackagingRules />} />
                                                      <Route path="/settings/data" element={<DataAndBackups />} />
                                                      <Route path="inspiration" element={<InspirationPage />} />
                                                      <Route path="templates" element={<TemplatesPage />} />
                                                      <Route path="debug/storage" element={<StorageDebug />} />
                                                      <Route path="journal" element={<Journal isDark={settings?.isDarkMode} />} />
                                                      <Route path="markets-checklists" element={<MarketsChecklistPage isDark={settings?.isDarkMode} />} />
                                                      <Route path="explore" element={<ExplorePage />} />
                                                      
                                                      {/* 404 - Show not found page instead of redirecting */}
                                                      <Route path="*" element={<NotFound />} />
                                                    </Routes>
                                                  </React.Suspense>
                                                </ErrorBoundary>
                                              </main>
                                              </div>

                                              <TimerTray />
                                              <Footer />
                                              <LowStockSuggester />
                                              <HelpDrawer />
                                              {/* AI Assistant - Only for active subscribers/trial users */}
                                              {isUnlocked && (
                                                <MudoAssistant context="The user is in the Studio world (paid), working with Planner, Kiln, Workshops and Shop." />
                                              )}

                                              {workspaceTag}
                                              <ToastHub />
                                              {studioSetupError && (
                                                <div className="fixed top-4 right-4 z-[12000] bg-red-600/80 text-white text-xs px-3 py-2 rounded shadow-lg">
                                                  {studioSetupError}
                                                </div>
                                              )}
                                              {!termsLoading && needsAcceptance && (
                                                <TermsModal
                                                  open
                                                  forceAccept
                                                  onAccept={async () => {
                                                    await acceptTerms();
                                                  }}
                                                />
                                              )}
                                            </div>
                                          </AssistantProvider>
                                      </InspirationProvider>
                                    </StudioSpaceProvider>
                                  </PackagingProvider>
                                </ParticipantProvider>
                              </WorkshopProvider>
                            </POProvider>
                          </MarketingProvider>
                        </UtilitiesProvider>
                      </KilnProvider>
                    </StaffProvider>
                  </MaterialProvider>
                  </VoiceTimerProvider>
                </SpeechProvider>
              </TasksProvider>
            </PlannerProvider>
            </StreaksProvider>
            </ForumSubscriptionsProvider>
          </NotificationsProvider>
          </FeedbackProvider>
        </PricingSettingsProvider>
      </PublicStudioProvider>
    </StudioProvider>
  );
}
